import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CustomizationService } from '@app/core/services/customization.service';
import { ImageService } from '@app/core/services/image.service';
import { PromotionService } from '@app/core/services/promotion.service';
import * as fromRoot from '@app/reducers';
import { Promotion } from '@app/shared/models/promotion';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { defer, forkJoin, from, Observable, of } from 'rxjs';
import { catchError, concatMap, exhaustMap, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import * as fromUploader from '../reducers';
import { PrepareActions } from '../actions';
import { Kyc, errors } from '@smash-sdk/kyc/ejs/11-2023';
import { Image } from '@smash-sdk/image/ejs/10-2019/image';
import { genericRetry } from '@app/shared/operators/genericRetry';
import { Constant } from 'src/constant';
import GetEmailChallengeError from '@smash-sdk/kyc/ejs/11-2023/types/GetEmailChallenge/GetEmailChallengeError';
import CreateEmailChallengeError from '@smash-sdk/kyc/ejs/11-2023/types/CreateEmailChallenge/CreateEmailChallengeError';
import VerifyChallengeError from '@smash-sdk-private/captcha/ejs/10-2025/types/VerifyChallenge/VerifyChallengeError';
import { Captcha } from '@smash-sdk-private/captcha/ejs/10-2025';
@Injectable()
export class PrepareEffects {

  addBackgroundImageToAttach = createEffect(() =>
    this.actions$.pipe(
      ofType(PrepareActions.addBackgroundImageToAttach),
      map(action => action.image),
      concatMap(([image]: any[]) => {
        const imageSdk = new Image();
        return forkJoin([of(image), from(imageSdk.createImage())])
      }),
      concatMap(([image, res]: any[]) => forkJoin([of(res), this.imageService.uploadImage(res.image.uploadUrl, image)])),
      concatMap(([res, _]: any[]) => {
        const background = { id: res.image.id, sourceUrl: res.image.downloadUrl, thumbnail: res.image.downloadUrl, type: 'Image' };
        return of(PrepareActions.addBackgroundImageToAttachSuccess({ background }));
      })
    )
  );

  addBackgroundVideoToAttach = createEffect(() =>
    this.actions$.pipe(
      ofType(PrepareActions.addBackgroundVideoToAttach),
      map(action => action.video),
      concatMap((video: any) => {
        const { type, sourceId } = video;
        if (type === 'Youtube') {
          const thumbnail = 'i1.ytimg.com/vi/' + sourceId + '/2.jpg';
          const videoToReturn = { ...video, thumbnail };
          return of(PrepareActions.addBackgroundVideoToAttachSuccess({ background: videoToReturn }));
        } else if (type === 'Vimeo') {
          return forkJoin([this.promotionService.getThumbnailVimeo(video.sourceId)]).pipe(map(([res]) => {
            const thumbnail = res[0].thumbnail_medium.replace('https://', '');
            const videoToReturn = { ...video, thumbnail };
            return PrepareActions.addBackgroundVideoToAttachSuccess({ background: videoToReturn });
          }));
        }
      })
    )
  );

  addLogoToAttach = createEffect(() =>
    this.actions$.pipe(
      ofType(PrepareActions.addLogoToAttach),
      map(action => action.image),
      concatMap(([image]: any[]) => {
        const imageSdk = new Image();
        return forkJoin([of(image), from(imageSdk.createImage())])
      }),
      concatMap(([image, res]: any[]) => forkJoin([of(res), this.imageService.uploadImage(res.image.uploadUrl, image)])),
      concatMap(([res, _]: any[]) => {
        const logo = { id: res.image.id, sourceUrl: res.image.downloadUrl, thumbnail: res.image.downloadUrl, type: 'Image' };
        return of(PrepareActions.addLogoToAttachSuccess({ logo }));
      })
    )
  );

  loadCustomization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrepareActions.loadCustomization),
      withLatestFrom(this.store$.pipe(select(fromRoot.getCurrentDomain))),
      exhaustMap(([_, domain]) => {
        return this.customizationService.listManagedCustomizations().pipe(mergeMap(({ customizations: managedCustomizations }) => {
          return this.customizationService.getManagedCustomization(managedCustomizations[0]?.id).pipe(mergeMap(({ customization: managedCustomization }) => {
            if (domain?.team) {
              return this.customizationService.listTeamCustomizations(domain.team).pipe(
                mergeMap(({ customizations: teamCustomizations }) => {
                  if (teamCustomizations?.length) {
                    return this.customizationService.getTeamCustomization(domain.team, teamCustomizations[0].id).pipe(
                      mergeMap(({ customization }) => {
                        if (customization?.logo && customization?.background) {
                          return [
                            PrepareActions.loadCustomizationSuccess({ customization })
                          ];
                        } else {
                          const logo = customization.logo ? customization.logo : managedCustomization.logo;
                          const background = customization.background ? customization.background : managedCustomization.background;
                          return [
                            PrepareActions.loadCustomizationSuccess({ customization: { ...customization, logo, background } })
                          ];
                        }
                      }));
                  } else {
                    return [
                      PrepareActions.loadCustomizationSuccess({ customization: managedCustomization })
                    ];
                  }
                }),
                catchError(error => {
                  return [
                    PrepareActions.loadCustomizationSuccess({ customization: managedCustomization })
                  ]
                }));
            } else {
              return [
                PrepareActions.loadCustomizationSuccess({ customization: managedCustomization })
              ];
            }
          }),
            catchError(error => {
              return [PrepareActions.loadCustomizationFailure({ error: error.message })]
            }));
        }));
      })
    )
  );

  loadCustomizationSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrepareActions.loadCustomizationSuccess),
      map(({ customization }) => PrepareActions.SetTransferCustomization({ logo: customization.logo, background: customization.background }))
    )
  );

  loadBackgrounds$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrepareActions.loadBackgrounds),
      withLatestFrom(this.store$.pipe(select(fromRoot.getCurrentDomain))),
      exhaustMap(([_, domain]) => {
        return this.customizationService.listManagedBackgrounds().pipe(mergeMap(({ backgrounds: managedBackgrounds }) => {
          if (domain?.team) {
            return this.customizationService.listTeamBackgrounds(domain.team).pipe(
              mergeMap((({ backgrounds }) => {
                if (backgrounds?.length) {
                  return of(PrepareActions.loadBackgroundsSuccess({ backgrounds }));
                } else {
                  return of(PrepareActions.loadBackgroundsSuccess({ backgrounds: managedBackgrounds }));
                }
              })),
              catchError(error => {
                return [PrepareActions.loadBackgroundsSuccess({ backgrounds: managedBackgrounds })]
              }));
          } else {
            return of(PrepareActions.loadBackgroundsSuccess({ backgrounds: managedBackgrounds }));
          }
        }));
      }))
  );

  loadLogos$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrepareActions.loadLogos),
      withLatestFrom(this.store$.pipe(select(fromRoot.getCurrentDomain))),
      exhaustMap(([_, domain]) => {
        return this.customizationService.listManagedLogos().pipe(mergeMap(({ logos: managedLogos }) => {
          if (domain?.team) {
            return this.customizationService.listTeamLogos(domain.team).pipe(
              mergeMap((({ logos }) => {
                if (logos?.length) {
                  return of(PrepareActions.loadLogosSuccess({ logos }));
                } else {
                  return of(PrepareActions.loadLogosSuccess({ logos: managedLogos }));
                }
              })),
              catchError(error => {
                return [PrepareActions.loadLogosSuccess({ logos: managedLogos })]
              }));
          } else {
            return of(PrepareActions.loadLogosSuccess({ logos: managedLogos }));
          }
        }));
      }))
  );

  loadExternalPromotions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrepareActions.loadExternalPromotions),
      withLatestFrom(
        this.store$.select(fromRoot.getCurrentDomain),
        this.store$.select(fromRoot.getSelectedLanguage),
      ),
      concatMap(([_, domain, language]) => {
        let req: Observable<{ promotions: Promotion[] }>;
        if (domain.team) {
          req = this.promotionService.listPromotions(domain.team);
        } else {
          req = this.promotionService.listManagedPromotions();
        }
        return req.pipe(
          mergeMap(({ promotions }) => {
            const filteredPromotions = promotions.filter(p => p.language === language);
            if (filteredPromotions.length && domain.team) {
              return [
                PrepareActions.selectRandomExternalPromotion({ promotions: filteredPromotions as [Promotion, ...Promotion[]] }),
              ];
            }
            return [PrepareActions.loadExternalPromotionsSuccess({ promotions: [] })];

          }),
          catchError(error => {
            return of(PrepareActions.loadExternalPromotionsFailure({ error: error.message }))
          })
        );
      })
    ));

  selectRandomExternalPromotion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrepareActions.selectRandomExternalPromotion),
      withLatestFrom(this.store$.select(fromRoot.getCurrentDomain)),
      concatMap(([action, domain]) => {
        const selectedPromotion = action.promotions[Math.floor(Math.random() * action.promotions.length)];
        return this.promotionService.getTeamPromotion(domain.team, selectedPromotion.id).pipe(
          mergeMap(({ promotion }) => [
            PrepareActions.selectRandomExternalPromotionSuccess({ promotion }),
            PrepareActions.loadExternalPromotionsSuccess({ promotions: action.promotions }),
            PrepareActions.SetTransferPromotion({ promotion }),
          ]),
          catchError(error => of(PrepareActions.loadPromotionFailure({ error: error.message })))
        );
      })
    ));

  loadPromotion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrepareActions.loadPromotion),
      withLatestFrom(this.store$.select(fromRoot.getCurrentDomain)),
      concatMap(([action, domain]) => {
        if (!domain?.team) {
          return [{ type: 'noop' }]
        }
        return this.promotionService.getTeamPromotion(domain.team, action.promotion.id).pipe(
          map((resp) => {
            return PrepareActions.loadPromotionSuccess({ promotion: resp.promotion });
          }),
          catchError((error: HttpErrorResponse) =>
            of(PrepareActions.loadPromotionFailure({ error: error.message }))
          )
        );
      })
    ));

  loadPromotionSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrepareActions.loadPromotionSuccess),
      map(({ promotion }) => PrepareActions.SetTransferPromotion({ promotion }))
    )
  );

  verifyCaptcha$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrepareActions.VerifyCaptcha),
      concatMap(({ token }) => {

        const captchaSdk = new Captcha();
        return defer(() => captchaSdk.verifyChallenge({ token, action: "upload" })).pipe(
          genericRetry({

            retryConfig: [
              ...Constant.NetworkErrorsRetryConfiguration,
              { error: VerifyChallengeError.InternalServerError, maxRetry: 0, exponentialBackoff: true },
              { error: VerifyChallengeError.GatewayTimeoutError, maxRetry: 10, exponentialBackoff: true },
              { error: VerifyChallengeError.BadGatewayError, maxRetry: 10, exponentialBackoff: true },
            ]
          }),
          concatMap(({ challenge }) => {
            return of(PrepareActions.VerifyCaptchaSuccess({ result: challenge.status }));
          }),
          catchError((error) => {
            return of(PrepareActions.VerifyCaptchaFailure({ error: error.message }));
          })
        )
      })
    )
  );

  verifyEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrepareActions.VerifyEmail),
      withLatestFrom(this.store$.select(fromRoot.getSelectedLanguage)),
      concatMap(([{ username }, language]) => {
        const kycSdk = new Kyc();
        return defer(() => kycSdk.getEmailChallenge({ email: username })).pipe(
          genericRetry({
            retryConfig: [
              ...Constant.NetworkErrorsRetryConfiguration,
              { error: GetEmailChallengeError.InternalServerError, maxRetry: 10, exponentialBackoff: true },
              { error: GetEmailChallengeError.GatewayTimeoutError, maxRetry: 10, exponentialBackoff: true },
              { error: GetEmailChallengeError.BadGatewayError, maxRetry: 10, exponentialBackoff: true },
            ]
          }),
          concatMap(({ emailChallenge }) => {
            if (emailChallenge.status === 'Dirty') {
              return [
                PrepareActions.SendEmailVerificationCode({ username }),
                PrepareActions.VerifyEmailSuccess({ status: 'Dirty' }),
              ];
            }
            return of(PrepareActions.VerifyEmailSuccess({ status: emailChallenge.status }));
          }),
          catchError((error) => {
            if (error instanceof errors.GetEmailChallengeError.NotFoundError) {
              return [
                PrepareActions.SendEmailVerificationCode({ username }),
                PrepareActions.VerifyEmailSuccess({ status: 'Dirty' }),
              ];
            }
            return of(PrepareActions.VerifyEmailFailure({ error: error.message }));
          })
        )
      })
    )
  );

  validateEmailVerificationCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrepareActions.ValidateEmailVerificationCode),
      concatMap(({ username, code }) => {
        const kycSdk = new Kyc();
        return defer(() => kycSdk.updateEmailChallenge({ email: username, token: code })).pipe(
          genericRetry({
            retryConfig: [
              ...Constant.NetworkErrorsRetryConfiguration,
              { error: errors.UpdateEmailChallengeError.InternalServerError, maxRetry: 10, exponentialBackoff: true },
              { error: errors.UpdateEmailChallengeError.GatewayTimeoutError, maxRetry: 10, exponentialBackoff: true },
              { error: errors.UpdateEmailChallengeError.BadGatewayError, maxRetry: 10, exponentialBackoff: true },
            ]
          }),
          map(({ emailChallenge }) => {
            return PrepareActions.ValidateEmailVerificationCodeSuccess({ status: emailChallenge.status });
          }),
          catchError((error) => {
            if (error instanceof errors.UpdateEmailChallengeError.InvalidTokenError) {
              return of(PrepareActions.ValidateEmailVerificationCodeFailure({ error: error.message }));
            }
            return of(PrepareActions.ValidateEmailVerificationCodeFailure({ error: error.message }));
          })
        );
      }),
      catchError((error: HttpErrorResponse) =>
        of(PrepareActions.ValidateEmailVerificationCodeFailure({ error: error.message }))
      )
    )
  );

  sendEmailVerificationCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrepareActions.SendEmailVerificationCode),
      withLatestFrom(this.store$.select(fromRoot.getSelectedLanguage)),
      concatMap(([{ username }, language]) => {
        const kycSdk = new Kyc();
        return defer(() => kycSdk.createEmailChallenge({ email: username, language })).pipe(
          genericRetry({
            retryConfig: [
              ...Constant.NetworkErrorsRetryConfiguration,
              { error: CreateEmailChallengeError.InternalServerError, maxRetry: 10, exponentialBackoff: true },
              { error: CreateEmailChallengeError.GatewayTimeoutError, maxRetry: 10, exponentialBackoff: true },
              { error: CreateEmailChallengeError.BadGatewayError, maxRetry: 10, exponentialBackoff: true },
            ]
          }),
          map(({ emailChallenge }) => {
            return PrepareActions.SendEmailVerificationCodeSuccess({ status: emailChallenge.status });
          }),
          catchError((error) => {
            if (error instanceof errors.CreateEmailChallengeError.NotificationAlreadySentError) {
              return of(PrepareActions.SendEmailVerificationCodeFailure({ error: error.message }));
            }
            return of(PrepareActions.SendEmailVerificationCodeFailure({ error: error.message }));
          })
        );
      }),
      catchError((error: HttpErrorResponse) =>
        of(PrepareActions.VerifyEmailFailure({ error: error.message }))
      )
    )
  );

  constructor(
    private actions$: Actions,
    private promotionService: PromotionService,
    private customizationService: CustomizationService,
    private store$: Store<fromUploader.State & fromRoot.State>,
    private imageService: ImageService,
  ) { }

}
